import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ChainedBackend from "i18next-chained-backend";
import HttpBackend from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";

// { en: { translation: { key: "val" }, otherNs?: {} }, fr: {...} }

const LOAD_PATH = "/virtual-i18n/{{lng}}/{{ns}}.json";
const LOCALSTORAGE_TTL_MS = 24 * 60 * 60 * 1000;

// Session-level cache to avoid multiple backend calls in same tab/session
let sessionResources = null;

export async function initI18n(initialLanguage = "en") {
  const { postRequest } = await import("./utils/api/requests/request");

  await i18n
    .use(ChainedBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: initialLanguage,
      fallbackLng: "en",
      ns: ["translation"],
      defaultNS: "translation",
      react: { useSuspense: false },
      interpolation: { escapeValue: false },
      load: "languageOnly",
      nonExplicitSupportedLngs: true,
      cleanCode: true,
      debug: false,

      backend: {
        backends: [LocalStorageBackend, HttpBackend],
        backendOptions: [
          // LocalStorageBackend options
          {
            expirationTime: LOCALSTORAGE_TTL_MS,
            store: window.localStorage,
            versions: {}, // add versions per lang if you want global invalidation
          },
          // HttpBackend options
          {
            loadPath: LOAD_PATH,
            request: async (_options, url, _payload, callback) => {
              try {
                const m = url.match(/\/virtual-i18n\/([^/]+)\/([^/.]+)\.json$/);
                const lang = m?.[1] || i18n.language || initialLanguage || "en";
                const ns = m?.[2] || "translation";

                // 1) If we already have everything in memory, serve from memory.
                if (sessionResources) {
                  const langData = sessionResources[lang]?.[ns] || {};
                  callback(null, {
                    status: 200,
                    data: JSON.stringify(langData),
                  });
                  return;
                }

                // 2) Otherwise, fetch ONCE from backend, transform, and seed caches
                const resp = await postRequest(
                  "RetrieveLanguageTableToolQuery",
                  {}
                );
                const rows = resp?.outData?.data?.rows || [];
                const resources = transformRowsToResources(rows);

                // Save to session memory
                sessionResources = resources;

                // Seed LocalStorage for ALL langs/namespaces so future switches are cache hits
                seedLocalStorageBundles(resources, LOCALSTORAGE_TTL_MS);

                // Seed i18next in-memory store too (so switching is instant in-session)
                seedI18nextStore(resources);

                // Respond only with the requested lang/ns (what http-backend expects)
                const langData = resources[lang]?.[ns] || {};
                callback(null, { status: 200, data: JSON.stringify(langData) });
              } catch (error) {
                // Last resort: return an empty object to avoid hard failure
                console.error("❌ i18n load error:", error);
                callback(error);
              }
            },
          },
        ],
      },

      detection: {
        order: ["localStorage", "querystring", "cookie", "navigator"],
        caches: ["localStorage", "cookie"],
        lookupQuerystring: "lng",
        lookupCookie: "i18next",
        lookupLocalStorage: "i18nextLng",
      },
    });

  return i18n;
}

/**
 * Convert DB rows => resources shape:
 * {
 *   en: { translation: { key: "value" } },
 *   fr: { translation: { key: "valeur" } }
 * }
 */
function transformRowsToResources(rows) {
  const resources = {};
  for (const row of rows || []) {
    if (!row?.key || !row?.values) continue;
    const ns = row.ns || "translation";
    for (const [lang, text] of Object.entries(row.values)) {
      if (!resources[lang]) resources[lang] = {};
      if (!resources[lang][ns]) resources[lang][ns] = {};
      if (text != null && String(text).trim() !== "") {
        resources[lang][ns][row.key] = String(text);
      }
    }
  }
  return resources;
}

/**
 * Write bundles to localStorage in the exact format expected by i18next-localstorage-backend:
 * Key:   i18next_res_<lng>_<ns>
 * Value: JSON.stringify({ ...bundle, i18nStamp: <epoch_ms> })
 */
function seedLocalStorageBundles(resources, ttlMs) {
  try {
    const now = Date.now();
    const stamp = now + ttlMs - 1000; // make it "fresh" for almost the full TTL
    for (const [lng, byNs] of Object.entries(resources)) {
      for (const [ns, bundle] of Object.entries(byNs)) {
        const key = `i18next_res_${lng}_${ns}`;
        const payload = JSON.stringify({ ...bundle, i18nStamp: stamp });
        window.localStorage.setItem(key, payload);
      }
    }
  } catch (e) {
    // If storage is full or disabled, just skip
    console.warn("⚠️ Unable to seed localStorage i18n bundles:", e);
  }
}

/**
 * Also seed i18next in-memory store so language switches are instant within this session.
 */
function seedI18nextStore(resources) {
  for (const [lng, byNs] of Object.entries(resources)) {
    for (const [ns, bundle] of Object.entries(byNs)) {
      // addResourceBundle respects deep merge if last param true
      if (!i18n.hasResourceBundle(lng, ns)) {
        i18n.addResourceBundle(lng, ns, bundle, true, true);
      } else {
        // Deep-merge if the bundle already exists
        const existing = i18n.getResourceBundle(lng, ns) || {};
        i18n.addResourceBundle(lng, ns, { ...existing, ...bundle }, true, true);
      }
    }
  }
}

/**
 * Helper to switch language. If target lang is missing, i18next will try LocalStorage first,
 * then hit our HttpBackend (which will be a no-op if sessionResources exists or LS is seeded).
 */
export async function setAppLanguage(lang) {
  await i18n.changeLanguage(lang);
  await i18n.reloadResources([lang]);
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", lang);
  }
}
